import moment from "moment";

import Utils, {
  getStorageKey,
  getCalendarFilter,
  nowUserTz,
  hasItems,
} from "../../../helpers/utils";

const DATE_FORMAT = "YYYY-MM-DD";

const updateReturnDate = (controller, timeslot) => {
  if (timeslot.time) {
    const startDate = moment(timeslot.time);

    const endDate = timeslot.endDate
      ? moment(timeslot.endDate)
      : startDate.clone().add(timeslot.duration, "minutes");
    const diffDays = endDate.diff(timeslot.startDate, "days");

    Utils.setStorageItem(
      getStorageKey(controller, "CONFIRMATION_DATE"),
      diffDays > 0 ? endDate.format(DATE_FORMAT) : timeslot.time,
    );
  }
};

const getExtendInterval = (timeslot) => {
  const oldEndTime = moment(timeslot.oldEndTime, "h:mma");
  const newEndTime = moment(timeslot.endTime, "h:mma");
  const oldStartTime = moment(timeslot.oldStartTime, "h:mma");
  const newStartTime = moment(timeslot.startTime, "h:mma");

  if (newEndTime.isBefore(newStartTime)) newEndTime.add(1, "days");
  if (oldEndTime.isBefore(oldStartTime)) oldEndTime.add(1, "days");

  const isGrowStart = Number(newStartTime.isBefore(oldStartTime));
  const isShrinkStart = Number(oldStartTime.isBefore(newStartTime));

  const isGrowEnd = Number(oldEndTime.isBefore(newEndTime));
  const isShrinkEnd = Number(newEndTime.isBefore(oldEndTime));

  return {
    isGrowStart,
    isShrinkStart,
    isGrowEnd,
    isShrinkEnd,
    extend_interval: isGrowEnd ? 1 : isShrinkEnd ? -1 : 0,
    extend_interval_start: isGrowStart ? 1 : isShrinkStart ? -1 : 0,
  };
};

const getAffectedTimeslotIds = (timeslot, allTimeslots) => {
  const beforeTime = timeslot.startTime
    ? moment(timeslot.startTime, "h:mma")
    : null;

  let startTime = moment(timeslot.time);
  const endTime = timeslot.endDate
    ? moment(timeslot.endDate)
    : startTime.clone().add(timeslot.duration, "minutes");
  if (beforeTime != null && beforeTime.isBefore(startTime)) {
    const newStartTime = startTime
      .clone()
      .set({ hour: beforeTime.hour(), minute: beforeTime.minute() });
    //TODO: day switch?
    startTime = newStartTime;
  }

  let timeslotIds = [];
  let timeslotIntervals = [];
  let duration = 0;
  allTimeslots.map((t) => {
    if (t != null) {
      const timeslotStartTime = moment(t.time);
      const timeslotEndTime = timeslotStartTime
        .clone()
        .add(t.duration, "minutes");

      if (
        timeslotStartTime.isBetween(startTime, endTime, "minutes", "[)") ||
        timeslotEndTime.isBetween(startTime, endTime, "minutes", "(]") ||
        (startTime.isBetween(timeslotStartTime, timeslotEndTime, "[)") &&
          endTime.isBetween(timeslotStartTime, timeslotEndTime, "(]"))
      ) {
        t.id.split(",").forEach((tid) => {
          if (!timeslotIds.includes(tid)) {
            timeslotIds.push(tid);
            duration += +t.duration;
          }
        });

        // Overlapping intervals
        if (t && t.activity) {
          const isIncluded =
            timeslot.id === t.id ||
            timeslot.id.startsWith(t.id + ",") ||
            timeslot.id.indexOf("," + t.id) >= 0;
          if (!isIncluded) {
            timeslotIntervals = [
              ...timeslotIntervals,
              {
                start: timeslotStartTime.format("H:mma on MM/DD"),
                end: timeslotEndTime.format("H:mma on MM/DD"),
              },
            ];
          }
        }
      }
    }
  });

  return { timeslotIds, timeslotIntervals, duration };
};

const _hasSamePredictions = (t, prevTimeslot) => {
  return (
    hasItems(t.predicted_activities) &&
    hasItems(prevTimeslot.predicted_activities) &&
    prevTimeslot.predicted_activities[0].id === t.predicted_activities[0].id
  );
};

const _hasThresholdProbability = (t, prevTimeslot) => {
  const thresholdCollapse = 0.7;

  return (
    t.predicted_activities[0].prob >= thresholdCollapse || //with high probability
    (t.predicted_activities.length > 3 && //or ratio compared to next 2 options
      t.predicted_activities[0].prob /
        (t.predicted_activities[0].prob +
          t.predicted_activities[1].prob +
          t.predicted_activities[2].prob) >=
        thresholdCollapse &&
      t.predicted_activities[0].prob >=
        prevTimeslot.predicted_activities[0].prob * 0.5)
  );
};

const mergeTimeslot = (
  prevTimeslot,
  t,
  mergeAutoReview = false,
  minTimeslotSize = 0,
  expandedTimeslots = null,
  collapsePredictions = false,
  minutesInterval = 15,
) => {
  let merged = false;

  const strExpandedTimeslots =
    expandedTimeslots && expandedTimeslots.length > 0
      ? "," + expandedTimeslots.join(",") + ","
      : null;
  if (prevTimeslot != null && t && t.confirmed === prevTimeslot.confirmed) {
    let isSame = false;
    if (mergeAutoReview) {
      isSame = t.id_autoreview === prevTimeslot.id_autoreview;
    } else {
      //TODO: simplify
      const isSameActivityAndFillSource = (t &&
          prevTimeslot &&
          t.activity != null &&
          prevTimeslot.activity != null &&
          t.activity?.id == prevTimeslot.activity?.id &&
          t.activity2?.id == prevTimeslot.activity2?.id &&
          t.activity3?.id == prevTimeslot.activity3?.id &&
          t.auto_fill === prevTimeslot.auto_fill &&
          JSON.stringify(t.auto_fill_source) ===
          JSON.stringify(prevTimeslot.auto_fill_source));
      const isSamePredictions = (minTimeslotSize &&
          minTimeslotSize > 0 &&
          prevTimeslot.duration < minTimeslotSize &&
          prevTimeslot.activity == null &&
          t.activity == null && //unconfirmed
          _hasSamePredictions(t, prevTimeslot));
      const isSamePredThreshold = (collapsePredictions &&
          prevTimeslot.activity == null &&
          t.activity == null && //unconfirmed
          _hasSamePredictions(t, prevTimeslot) &&
          _hasThresholdProbability(t, prevTimeslot));

      isSame = isSameActivityAndFillSource || isSamePredictions || isSamePredThreshold;

      if (
        isSame &&
        /*(t.activity == null || t.auto_fill == null || t.auto_fill != AppConfig.AUTOTRACKED) &&*/
        strExpandedTimeslots != null &&
        (strExpandedTimeslots.indexOf("," + t.id + ",") >= 0 ||
          strExpandedTimeslots.indexOf("," + prevTimeslot.id + ",") >= 0)
      ) {
        isSame = false;
      }
    }
    // 2021.05.10: After tracking a manual entry of a weird timeslot
    // next interval should get user to closest interval timeslot ("11:22-11:30" not "11:22-11:45")
    if (
      isSame &&
      t.activity == null &&
      t.duration >= minutesInterval &&
      prevTimeslot?.duration < t.duration &&
      minTimeslotSize > 0 &&
      prevTimeslot?.duration % minutesInterval != 0
    ) {
      isSame = false;
    }
    if (
      isSame &&
      t.activity == null &&
      prevTimeslot.time.length > 5 &&
      prevTimeslot.time.substr(prevTimeslot.time.length - 5, 2) %
        Math.max(minutesInterval, Math.min(60, minTimeslotSize)) !=
        0
    ) {
      isSame = false;
    }

    if (isSame) {
      const startDate = moment(t.time);
      const prevEndDate = moment(prevTimeslot.time).add(
        prevTimeslot.duration,
        "minutes",
      );
      if (!prevEndDate.isBefore(startDate)) {
        prevTimeslot.id = prevTimeslot.id + "," + t.id;
        prevTimeslot.duration = prevTimeslot.duration + t.duration;

        if (prevTimeslot.note != null && t.note != null) {
          prevTimeslot.note =
            prevTimeslot.note.length > 0
              ? prevTimeslot.note.length + "\n" + t.note
              : t.note;
        } else if (prevTimeslot.note == null) {
          prevTimeslot.note = t.note;
        }

        prevTimeslot.endDate = t.endDate;
        prevTimeslot.endTime = t.endTime;
        prevTimeslot.oldEndTime = t.endTime;

        prevTimeslot.undo_activity = "undo";

        if (prevTimeslot.id_chunk !== t.id_chunk) {
          if (prevTimeslot.last_updated < t.last_updated) {
            prevTimeslot.id_chunk = t.id_chunk;
            prevTimeslot.undo_id = t.id;
          }
        } else {
          prevTimeslot.undo_id = prevTimeslot.undo_id + "," + t.id;
        }

        merged = true;
        //console.log(prevTimeslot);
      }
    }
  }
  return merged;
};

const move = (arr, old_index, new_index) => {
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr;
};

function replacePrediction(t, override, activities, source) {
  let act = activities[override];

  //starting with []
  if (act == null && override.length > 0 && override[0] === "[") {
    const i = override.indexOf("]");
    if (i > 0) {
      override = override.substring(1, i).trim();
      act = activities[override];
    }
  }
  //ending with []
  if (
    act == null &&
    override.length > 0 &&
    override[override.length - 1] === "]"
  ) {
    const i = override.indexOf("[");
    if (i > 0) {
      override = override.substring(i + 1, override.length - 1).trim();
      act = activities[override];
    }
  }

  if (act == null && override.length >= 3) {
    const name = override.toLowerCase();
    Object.keys(activities).forEach((a) => {
      if (name.startsWith(a.toLowerCase()) || name.endsWith(a.toLowerCase())) {
        act = activities[a];
      }
    });
  }

  if (act != null) {
    let found = -1;
    for (var i = 0; i < t.predicted_activities.length; i++) {
      const p = t.predicted_activities[i];
      if (p.name == act.name && found < 0) {
        found = i;
        break;
      }
    }

    if (found < 0) {
      t.predicted_activities.unshift({
        prob: 1,
        color: act.color,
        name: act.name,
        icon: act.icon,
        id: act.id,
        source: source,
      });
    } else if (found > 0) {
      move(t.predicted_activities, found, 0);
      t.predicted_activities[0].source = source;
    }
    return true;
  } else {
    return false;
  }
}

const overrideCalendar = (controller, timeslots) => {
  if (
    controller.state.calendarItems != null &&
    controller.state.calendarItems.length > 0
  ) {
    const activities = {};
    const activitiesById = {};
    controller.state.elements.forEach((a) => {
      activities[a.name] = a;
      activitiesById[a.id] = a;
    });

    const filter = getCalendarFilter(controller, null);

    //temp hacky optimization
    if (
      !controller.parsedCalendarData ||
      controller.parsedCalendarData?.calendarItemsChecksum !==
        controller.state.calendarItemsChecksum
    ) {
      const items = JSON.parse(controller.state.calendarItems);
      if (items != null)
        items.sort((a, b) => {
          return a.start < b.start ? -1 : a.start > b.start ? 1 : 0;
        });
      controller.parsedCalendarData = {
        calendarItemsChecksum: controller.state.calendarItemsChecksum,
        items: items,
      };
    }
    const enabledIntegrations = controller.state.settings?.integrations;
    const items = (controller.parsedCalendarData.items || []).filter((c) => {
      if (c.calId != null) {
        const calKey = "calendar:" + c.calId.substring(0, c.calId.indexOf(" "));
        return enabledIntegrations[calKey];
      } else {
        return false;
      }
    });

    const probableEvents = controller.state.calendar_prob;
    let idxStart = 0;
    let idxEnd = items.length;

    const tz = controller.state.settings?.tz_override;
    const deltaMin = nowUserTz(controller).utcOffset() - moment().utcOffset();

    //console.log(items);
    timeslots.forEach((t) => {
      if (t && t.activity == null && t.predicted_activities != null) {
        const startDateTime = moment(t.time);
        const endDateTime = startDateTime.clone().add(t.duration, "minutes");

        try {
          for (let k = idxStart; k < idxEnd; k++) {
            const it = items[k];

            if (it.start != null && it.end != null) {
              it.calStart =
                it.calStart || moment(it.start).add(deltaMin, "minutes");
              it.calEnd = it.calEnd || moment(it.end).add(deltaMin, "minutes");

              const calStart = it.calStart;
              const calEnd = it.calEnd;

              if (startDateTime.isAfter(calEnd)) {
                idxStart = k;
              } else if (calStart.isAfter(endDateTime)) {
                break;
              } else if (
                startDateTime.isBetween(calStart, calEnd, "minutes", "[)") ||
                endDateTime.isBetween(calStart, calEnd, "minutes", "(]")
              ) {
                if (filter != null) {
                  const p1 = it.calId.indexOf(":");
                  const p2 = it.calId.indexOf(" ");
                  const cid =
                    it.calId.substring(0, p1 + 1) + it.calId.substring(p2 + 1);
                  if (filter.includes(cid)) {
                    continue;
                  }
                }

                //console.log("Calendar time match");
                //console.log(it)

                let override = it.name;
                const isReplaced = replacePrediction(
                  t,
                  override,
                  activities,
                  "cal",
                );
                if (isReplaced) throw override;

                if (it.description) {
                  override = it.description;
                  const isReplaced = replacePrediction(
                    t,
                    override,
                    activities,
                    "cal",
                  );
                  if (isReplaced) throw override;
                }

                if (probableEvents) {
                  const idActivity = probableEvents[it.name];
                  if (idActivity) {
                    const isReplaced = replacePrediction(
                      t,
                      idActivity,
                      activitiesById,
                      "cal",
                    );
                    if (isReplaced) throw override;
                  }
                }
              }
            }
          }
        } catch (override) {
          //console.log("Calendar override " + t.time + " for " + override);
        }
      }
    });
  }
};

const forceSequences = (controller, timeslots) => {
  const seq = controller.state.sequences;
  if (seq != null && controller.state.elements != null) {
    const activities = {};
    controller.state.elements.forEach((a) => {
      activities[a.id] = a;
    });

    const HOUR_BAND = 3;
    const MAX_SEQ_MARKER = -1;

    let prevTimeslot = null;
    timeslots.forEach((t) => {
      if (
        prevTimeslot != null &&
        prevTimeslot.activity != null &&
        prevTimeslot.activity.id != null &&
        t != null &&
        t.activity == null &&
        t.predicted_activities != null
      ) {
        const sequences = seq[prevTimeslot.activity.id];
        if (
          sequences != null &&
          prevTimeslot.duration <= sequences[MAX_SEQ_MARKER]
        ) {
          let prevIt = null;
          for (var it in sequences) {
            if (it != MAX_SEQ_MARKER) {
              if (it > prevTimeslot.duration) break;
              prevIt = it;
            }
          }
          if (prevIt != null && prevIt != MAX_SEQ_MARKER) {
            const override = sequences[prevIt];
            if (override != null && override.length > 0) {
              const daySeq = Math.floor(moment(t.time).hour() / HOUR_BAND);
              const isProbable =
                controller.state.probable_times &&
                controller.state.probable_times[override] &&
                controller.state.probable_times[override][daySeq] > 0;
              if (isProbable) {
                replacePrediction(t, override, activities, "seq");
              }
            }
          }
        }
      }
      prevTimeslot = t;
    });
  }
};

const biasPredictions = (controller, timeslots) => {
  forceSequences(controller, timeslots);
  if (
    controller.state.settings?.integrations?.calendar_google ||
    controller.state.settings?.integrations?.calendar_native
  ) {
    overrideCalendar(controller, timeslots);
  }
};

function buildConfirmArgs(timeslot, allTimeslots) {
  const { source } = timeslot;

  let { timeslotIds } = getAffectedTimeslotIds(timeslot, allTimeslots);

  const {
    isGrowStart,
    isShrinkStart,
    isGrowEnd,
    isShrinkEnd,
    extend_interval,
    extend_interval_start,
  } = getExtendInterval(timeslot);

  // On shrink send all previous ids
  if (timeslotIds.length > 0 && (isShrinkStart || isShrinkEnd)) {
    const t = timeslot.id.split(",");
    let idx = 0;
    let concatenated = [];
    while (idx < t.length) {
      if (timeslotIds.includes(t[idx])) break;
      concatenated.push(t[idx]);
      idx++;
    }
    concatenated = concatenated.concat(timeslotIds);
    while (idx < t.length) {
      if (t[idx] == null || (t[idx] && !timeslotIds.includes(t[idx]))) break;
      idx++;
    }
    while (idx < t.length) {
      if (t[idx] && !concatenated.includes(t[idx])) concatenated.push(t[idx]);
      idx++;
    }
    timeslotIds = concatenated;
  }

  const args = {
    id: timeslotIds.join(","),
    start_time: timeslot.time,
    extend_interval,
    extend_interval_start,
    source,
  };
  return { timeslotIds, args };
}

const confirmMultipleElements = (
  controller,
  timeslot,
  elements,
  callback = () => {},
) => {
  const allTimeslots = controller.state.timeslots || [];
  let { timeslotIds, args } = buildConfirmArgs(timeslot, allTimeslots);

  controller.apiService
    .cardAction({
      ...args,
      action: "confirm_multiple",
      id: timeslotIds.join(","),
      start_time: timeslot.time,
      end_time: timeslot.endDate ? timeslot.endDate : null,
      elements,
    })
    .then(callback);
};

const saveTimeslotToDB = (
  controller,
  allTimeslots = [],
  {
    timeslot,
    is_confirmed = true,
    was_predicted = 0,
    was_first_option = 0,
    reload = true,
    undoAll = false,
    sourceOverride = null,
  },
  callback = () => {},
  refreshFromRemote = true
) => {
  const { activity, source } = timeslot;

  let { timeslotIds, args } = buildConfirmArgs(timeslot, allTimeslots);

  if (!Utils.hasItems(timeslotIds)) {
    console.log("no timeslot ids found");
    return;
  }

  const action = is_confirmed
    ? {
        ...args,
        action: "next",
        type: activity?.id,
        activity_details: activity,
        source: sourceOverride || source,
        was_predicted,
        was_first_option,
        debug: {
          time_picker_start_time: timeslot.startTime,
        },
      }
    : {
        action: "undo",
        // Undo only last timeslot in chunk
        id: undoAll ? timeslotIds[0] : timeslotIds[timeslotIds.length - 1],
      };

  // Set end time for affected timeslots
  if (timeslot.endDate) action.end_time = timeslot.endDate;
  if (timeslot.endTime) {
    action.debug = {
      time_picker_start_time: timeslot.startTime,
      time_picker_end_time: timeslot.endTime,
    };
  }

  // For returning to last edited timeslot
  if (is_confirmed) updateReturnDate(controller, timeslot);

  console.log(action);

  controller.tracking.track("Journal");
  console.log("Journal");
  controller.cardActionWithCallback(action, () => {
        if (reload) callback();
      },
      null,
      refreshFromRemote
  );
};

const filterTimeslotsByInterval = (timeslots, selectedDate, days = 0) => {
  const startDate = moment(selectedDate);
  const endDate = startDate.clone().add(days, "days");

  let intervalTimeslots = [];
  timeslots
    .filter((t) => t && t.activity)
    .forEach((t) => {
      const ts_date = moment(t.time);
      if (ts_date.isBetween(startDate, endDate, "minutes", "[)")) {
        intervalTimeslots.push(t);
      }
    });

  return intervalTimeslots;
};

const clearBulkTimeslots = (
  controller,
  timeslotIds = [],
  callback = () => {},
) => {
  controller.apiService
    .cardAction({
      action: "bulk-clear",
      clearTimeslots: timeslotIds,
    })
    .then(() => {
      callback();
    });
};

const JournalHelper = {
  getAffectedTimeslotIds,
  saveTimeslotToDB,
  confirmMultipleElements,
  getExtendInterval,
  mergeTimeslot,
  biasPredictions,
  filterTimeslotsByInterval,
  clearBulkTimeslots,
};

export default JournalHelper;
